import * as React from "react";

import { formatShortDateTime, useDateTimeFormat } from "@/shared/datetime";
import { Tooltip } from "@/shared/foundation/primitives";
import type { ICellRendererParams } from "@/shared/foundation/primitives/Table";
import { SubstringHighlight } from "@/shared/text/SubstringHighlight";

import { formatMetricValue, withUnit } from "./formatMetricValue";
import styles from "./MetricCells.module.css";
import { recordCount, type MetricTreeRow } from "./metricLeaves";
import { MetricSearchContext } from "./metricSearchContext";

type MetricCellParams = ICellRendererParams<MetricTreeRow>;

/**
 * What an empty part of a name is shown and read as: the API takes names
 * such as `.imu`, `imu.` and `a..b`, whose parts the table still lists apart.
 */
const EMPTY_PART = "(empty)";

/**
 * A row's last name part: a group's in medium weight, a metric's in regular,
 * with the full dotted name in a tooltip. A screen reader hears the same: the
 * part first, then the group it is in ("max from cpu.load"). An empty part
 * shows as "(empty)" in secondary text.
 */
export function MetricNameCell({ data: row }: MetricCellParams) {
  const searchText = React.useContext(MetricSearchContext);
  if (!row) {
    return null;
  }
  // The search matches full names, so match it against this row's full name
  // and mark only the part that falls in the row's own segment.
  const fullName = row.kind === "leaf" ? row.row.name : row.path;
  const lastDot = fullName.lastIndexOf(".");
  const parent = lastDot === -1 ? null : fullName.slice(0, lastDot);
  return (
    <Tooltip>
      <Tooltip.Trigger
        render={
          <span
            className={row.kind === "leaf" ? styles.name : styles.groupName}
          >
            {row.label === "" ? (
              <span className={styles.emptyPart}>{EMPTY_PART}</span>
            ) : (
              <SubstringHighlight
                text={fullName}
                slice={[fullName.length - row.label.length, fullName.length]}
                query={searchText}
                markClassName={styles.highlight}
              />
            )}
            {parent === null ? null : (
              <span className="sr-only"> from {parent || EMPTY_PART}</span>
            )}
          </span>
        }
      />
      <Tooltip.Content className={styles.nameTooltip}>
        {fullName === "" ? EMPTY_PART : breakableName(fullName)}
      </Tooltip.Content>
    </Tooltip>
  );
}

/** `name` with a line break allowed after each dot, so it wraps between parts. */
function breakableName(name: string): React.ReactNode {
  return name.split(".").map((part, index, parts) => (
    <React.Fragment key={index}>
      {part}
      {index < parts.length - 1 ? (
        <>
          .<wbr />
        </>
      ) : null}
    </React.Fragment>
  ));
}

/** How many records a metric holds, or a group's metrics between them. */
export function RecordsCell({ data: row }: MetricCellParams) {
  if (!row) {
    return null;
  }
  return (
    <span>
      {row.kind === "leaf" ? row.row.records.length : recordCount(row.leaves)}
    </span>
  );
}

/** A metric's newest value, rounded, with the full value in a tooltip. */
export function ValueCell({ data: row }: MetricCellParams) {
  const latest = row?.kind === "leaf" ? row.row.latest : undefined;
  if (!latest) {
    return null;
  }
  const { value, unit } = latest;
  return (
    <Tooltip>
      <Tooltip.Trigger
        render={
          <span className={styles.value}>
            {withUnit(formatMetricValue(value), unit)}
          </span>
        }
      />
      <Tooltip.Content>{withUnit(String(value), unit)}</Tooltip.Content>
    </Tooltip>
  );
}

/**
 * When a metric's newest value was published, short ("Oct 29, 11:32 AM") so
 * it fits the column on one line, with the full timestamp in a tooltip. The
 * time follows the viewer's 12/24-hour preference.
 */
export function PublishedCell({ data: row }: MetricCellParams) {
  const format = useDateTimeFormat();
  const latest = row?.kind === "leaf" ? row.row.latest : undefined;
  if (!latest) {
    return null;
  }
  const published = new Date(latest.published);
  if (Number.isNaN(published.getTime())) {
    return null;
  }
  return (
    <Tooltip>
      <Tooltip.Trigger
        render={
          <time dateTime={published.toISOString()} className={styles.published}>
            {formatShortDateTime(published, format)}
          </time>
        }
      />
      <Tooltip.Content>
        {format.format(published, { time: "seconds", timeZone: true })}
      </Tooltip.Content>
    </Tooltip>
  );
}
